import * as React from 'react';
import { forwardRef, useState } from 'react';
import Spinner from 'react-spinkit';
import Dropdown from 'react-bootstrap/Dropdown';
import FormControl from 'react-bootstrap/FormControl';
import cx from 'classnames';
import { Icon } from '@iconify/react';
import styles from './CustomDropdown.module.scss';

interface Props {
  onSelectSubject: (arg0: string | null) => void;
  initialValue: string;
  currentlySelectedItem: string;
  disableChangeLocation: boolean;
  list: { id: string; name: string }[];
  icon: string;
  noFilter?: boolean;
}

interface CustomToggleProps {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  disabled: boolean;
}

interface CustomMenuProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  className: string;
  'aria-labelledby'?: string;
}

export const CustomToggle = forwardRef<HTMLDivElement, CustomToggleProps>((props: CustomToggleProps, ref) => {
  const { children, onClick, disabled } = props;
  if (disabled) {
    return (
      <div ref={ref} className={cx(styles.customToggle, styles.customToggleDisabled)}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={styles.customToggle}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </div>
  );
});
CustomToggle.displayName = 'CustomToggle';

export const CustomMenu = forwardRef<HTMLDivElement, CustomMenuProps>((props: CustomMenuProps, ref) => {
  const { children, style, className, 'aria-labelledby': labeledBy } = props;
  const [value, setValue] = useState('');

  return (
    <div ref={ref} style={style} className={className} aria-labelledby={labeledBy}>
      <FormControl
        autoFocus
        className={styles.formControl}
        placeholder='Type to filter...'
        onChange={(e) => setValue(e.target.value.toLowerCase())}
        value={value}
      />
      <ul className='list-unstyled'>
        {React.Children.toArray(children).filter((child) => {
          if (React.isValidElement(child) && typeof child.props.children === 'string')
            return !value || child.props.children.toLowerCase().includes(value);
          return false;
        })}
      </ul>
    </div>
  );
});
CustomMenu.displayName = 'CustomMenu';

const CustomDropdown = ({
  onSelectSubject,
  initialValue,
  currentlySelectedItem,
  disableChangeLocation,
  list,
  icon,
  noFilter,
}: Props): JSX.Element => {
  if (!initialValue) {
    return (
      <div className={styles.selectorErrorMessage}>
        <Spinner name='line-scale' />
      </div>
    );
  }
  const initialObject = list.find((e) => e.id === initialValue);
  const initialDisplayValue = (initialObject && initialObject.name) || list[0].name;
  return (
    <div
      className={cx(styles.selecter, {
        [`${styles.disabledSelector}`]: disableChangeLocation,
      })}
    >
      <Dropdown onSelect={onSelectSubject}>
        <Dropdown.Toggle as={CustomToggle} id='dropdown-custom-components' disabled={disableChangeLocation}>
          <Icon className={styles.infoGraphicIcon} icon={icon} />
          {initialDisplayValue}
          <Icon className={styles.arrowDownIcon} icon='bxs:down-arrow' />
        </Dropdown.Toggle>

        <Dropdown.Menu as={noFilter ? undefined : CustomMenu} className={styles.dropdownMenu}>
          {list &&
            list.map((e) => {
              if (currentlySelectedItem && e.id === currentlySelectedItem) {
                return (
                  <Dropdown.Item as='button' active eventKey={e.id} key={e.id}>
                    {e.name}
                  </Dropdown.Item>
                );
              }

              return (
                <Dropdown.Item as='button' eventKey={e.id} key={e.id}>
                  {e.name}
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CustomDropdown;
